import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axios from "axios";
import config from "../config.js";


const AdjustmentEntry = ({ journalEntries, onDelete }) => {
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, entryId: null, entryDetails: null });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {API_BASE_URL} = config;
  

  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success' 
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));


  const showToast = (message, severity = 'success') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  const hideToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast(prev => ({ ...prev, open: false }));
  };

  const handleDeleteClick = (entryId, entryDetails) => {
    setDeleteDialog({
      open: true,
      entryId,
      entryDetails
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.entryId) return;

    setIsDeleting(true);
    try {
      
      if (onDelete) {
        await onDelete(deleteDialog.entryId);
      }
      
      setDeleteDialog({ open: false, entryId: null, entryDetails: null });
      showToast('Journal entry deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting entry:', error);
      showToast('Failed to delete entry. Please try again.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, entryId: null, entryDetails: null });
  };
 const downloadJournalEntriesExcel = async () => {
  // Use selectedCompanyId as kycId since they represent the same thing
  const kycId = selectedCompanyId;
  
  if (!kycId) {
    showToast('KYC ID is required', 'error');
    return;
  }

  setIsDownloading(true);
  
  try {
    const response = await axios({
      method: 'GET',
      url: `${API_BASE_URL}/api/v1/journalentriesExcel/${kycId}`,
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    
    // Set filename
    const filename = `journal_entries_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.setAttribute('download', filename);
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    showToast('Excel file downloaded successfully', 'success');
    
  } catch (error) {
    console.error('Download error:', error);
    
    // Simple error handling
    let message = 'Failed to download Excel file';
    if (error.response?.status === 404) {
      message = 'No journal entries found';
    } else if (error.response?.status === 400) {
      message = 'Invalid KYC ID';
    } else if (!error.response) {
      message = 'Network error. Check your connection';
    }
    
    showToast(message, 'error');
  } finally {
    setIsDownloading(false);
  }
};
  const MobileEntryCard = ({ entry, index }) => (
    <Card 
      key={entry._id} 
      sx={{ 
        mb: 2, 
        borderRadius: 2,
        boxShadow: 1,
        border: '1px solid',
        borderColor: 'grey.200'
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Journal #{entry.journalNumber}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Download Excel" arrow>
              <IconButton
                color="primary"
                onClick={downloadJournalEntriesExcel}
                size="small"
                disabled={isDownloading}
              >
                {isDownloading ? <CircularProgress size={20} /> : <FileDownloadIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Entry" arrow>
              <IconButton
                color="error"
                onClick={() => handleDeleteClick(entry._id, {
                  journalNumber: entry.journalNumber,
                  date: entry.entryDate,
                  invoiceNo: entry.invoiceNo
                })}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Date:</Typography>
            <Typography variant="body2">{new Date(entry.entryDate).toLocaleDateString()}</Typography>
          </Box>
          
          {entry.invoiceNo && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Invoice No:</Typography>
              <Typography variant="body2">{entry.invoiceNo}</Typography>
            </Box>
          )}
          
          {entry.invoiceDate && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Invoice Date:</Typography>
              <Typography variant="body2">{new Date(entry.invoiceDate).toLocaleDateString()}</Typography>
            </Box>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Ledger Entries:
        </Typography>
        
        {entry.ledgerEntries.map((ledgerEntry, ledgerIndex) => (
          <Box 
            key={ledgerIndex}
            sx={{ 
              p: 1.5, 
              mb: 1, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {ledgerEntry.ledger}
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <Chip 
                label={`Debit: ₹${ledgerEntry.debit.toFixed(2)}`} 
                size="small" 
                color={ledgerEntry.debit > 0 ? "error" : "default"}
                variant={ledgerEntry.debit > 0 ? "filled" : "outlined"}
              />
              <Chip 
                label={`Credit: ₹${ledgerEntry.credit.toFixed(2)}`} 
                size="small" 
                color={ledgerEntry.credit > 0 ? "success" : "default"}
                variant={ledgerEntry.credit > 0 ? "filled" : "outlined"}
              />
            </Stack>
            
            {ledgerEntry.description && (
              <Typography variant="body2" color="text.secondary">
                {ledgerEntry.description}
              </Typography>
            )}
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ mb: 3, mr: { xs: 0, md: 3 } }}>
      {/* Header with Download Button */}
      {selectedCompanyId && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={isDownloading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            onClick={downloadJournalEntriesExcel}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download Excel'}
          </Button>
        </Box>
      )}

      {/* Mobile View */}
      {isMobile ? (
        <Box>
          {journalEntries && journalEntries.length > 0 ? (
            journalEntries.map((entry, index) => (
              <MobileEntryCard key={entry._id} entry={entry} index={index} />
            ))
          ) : (
            <Card sx={{ textAlign: 'center', py: 4 }}>
              <CardContent>
                <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No journal entries found
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        /* Desktop/Tablet Table View */
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Table 
            sx={{ 
              minWidth: isTablet ? 800 : 650,
              '& .MuiTableCell-root': {
                fontSize: isTablet ? '0.875rem' : '1rem'
              }
            }} 
            aria-label="journal entries table"
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Journal No.</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Invoice No.</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Invoice Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Ledger</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Debit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Credit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {journalEntries && journalEntries.length > 0 ? (
                journalEntries.map((entry, index) => (
                  <React.Fragment key={entry._id}>
                    {entry.ledgerEntries.map((ledgerEntry, ledgerIndex) => (
                      <TableRow 
                        key={`${entry._id}-${ledgerIndex}`}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: 'grey.50' 
                          },
                          borderBottom: '1px solid',
                          borderColor: 'grey.200'
                        }}
                      >
                        {ledgerIndex === 0 && (
                          <>
                            <TableCell 
                              rowSpan={entry.ledgerEntries.length} 
                              sx={{ 
                                py: 2, 
                                px: isTablet ? 1 : 2,
                                borderRight: '1px solid',
                                borderColor: 'grey.200'
                              }}
                            >
                              {new Date(entry.entryDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell 
                              rowSpan={entry.ledgerEntries.length} 
                              sx={{ 
                                py: 2, 
                                px: isTablet ? 1 : 2,
                                borderRight: '1px solid',
                                borderColor: 'grey.200'
                              }}
                            >
                              {entry.journalNumber}
                            </TableCell>
                            <TableCell 
                              rowSpan={entry.ledgerEntries.length} 
                              sx={{ 
                                py: 2, 
                                px: isTablet ? 1 : 2,
                                borderRight: '1px solid',
                                borderColor: 'grey.200'
                              }}
                            >
                              {entry.invoiceNo || '-'}
                            </TableCell>
                            <TableCell 
                              rowSpan={entry.ledgerEntries.length} 
                              sx={{ 
                                py: 2, 
                                px: isTablet ? 1 : 2,
                                borderRight: '1px solid',
                                borderColor: 'grey.200'
                              }}
                            >
                              {entry.invoiceDate ? new Date(entry.invoiceDate).toLocaleDateString() : '-'}
                            </TableCell>
                          </>
                        )}
                        <TableCell sx={{ 
                          py: 2, 
                          px: isTablet ? 1 : 2, 
                          borderRight: '1px solid', 
                          borderColor: 'grey.200' 
                        }}>
                          {ledgerEntry.ledger}
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2, 
                          px: isTablet ? 1 : 2, 
                          borderRight: '1px solid', 
                          borderColor: 'grey.200', 
                          textAlign: 'right',
                          color: ledgerEntry.debit > 0 ? 'error.main' : 'text.primary',
                          fontWeight: ledgerEntry.debit > 0 ? 600 : 400
                        }}>
                          ₹{ledgerEntry.debit.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2, 
                          px: isTablet ? 1 : 2, 
                          borderRight: '1px solid', 
                          borderColor: 'grey.200', 
                          textAlign: 'right',
                          color: ledgerEntry.credit > 0 ? 'success.main' : 'text.primary',
                          fontWeight: ledgerEntry.credit > 0 ? 600 : 400
                        }}>
                          ₹{ledgerEntry.credit.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ 
                          py: 2, 
                          px: isTablet ? 1 : 2, 
                          borderRight: '1px solid', 
                          borderColor: 'grey.200',
                          maxWidth: isTablet ? 150 : 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          <Tooltip title={ledgerEntry.description || '-'} arrow>
                            <span>{ledgerEntry.description || '-'}</span>
                          </Tooltip>
                        </TableCell>
                        {ledgerIndex === 0 && (
                          <TableCell 
                            rowSpan={entry.ledgerEntries.length} 
                            align="center"
                            sx={{ py: 2, px: isTablet ? 1 : 2 }}
                          >
                            <Tooltip title="Delete Entry" arrow>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteClick(entry._id, {
                                  journalNumber: entry.journalNumber,
                                  date: entry.entryDate,
                                  invoiceNo: entry.invoiceNo
                                })}
                                size="small"
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s ease-in-out',
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={9} 
                    align="center"
                    sx={{ 
                      py: 4, 
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}
                  >
                    No journal entries found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this journal entry?
            {deleteDialog.entryDetails && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Journal Number:</strong> {deleteDialog.entryDetails.journalNumber}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {new Date(deleteDialog.entryDetails.date).toLocaleDateString()}</Typography>
                {deleteDialog.entryDetails.invoiceNo && (
                  <Typography variant="body2"><strong>Invoice No:</strong> {deleteDialog.entryDetails.invoiceNo}</Typography>
                )}
              </Box>
            )}
            <Typography variant="body2" sx={{ mt: 2, color: 'error.main' }}>
              This action cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast/Snackbar for notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={hideToast} 
          severity={toast.severity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdjustmentEntry;
